'use client';

import React, { forwardRef } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { FirestoreCollectionStats } from '@/hooks/useFirestoreCollectionStats';
import type { CollectionInsight } from '@/hooks/useCollectionAnalysis';
import type { AbstractFearGreed } from '@/hooks/useMarketAnalysis';

interface NFTAssetShareCardProps {
  stats: FirestoreCollectionStats;
  collectionName: string;
  collectionSlug: string;
  currency?: string;
  insight?: CollectionInsight | null;
  imageUrl?: string | null;
  abstractFearGreed?: AbstractFearGreed | null;
  isAbstractChain?: boolean;
  /**
   * Render context. 'page' (default) is the natural variable-height portrait used
   * in the on-site Share modal. 'tweet' is the crop-safe variant for the Daily
   * Spotlight image: a top brand bar (so the wordmark survives X's ~16:9 in-feed
   * crop) + a line-clamped AI insight (so the card height — and thus the image
   * aspect — stays locked near 4:5 instead of drifting with insight length).
   */
  format?: 'page' | 'tweet';
}

/**
 * Direction → brand palette. Design system: amber is the only accent, green for
 * positive, red for negative, slate for flat/neutral (0%). Used for the badge,
 * glow and corner accent so the whole card reads the direction at a glance.
 */
const DIRECTION_PALETTE = {
  up: {
    accentColor: '#22A45D',
    accentGradient: 'linear-gradient(135deg, #1B8049 0%, #22A45D 55%, #2ECC71 100%)',
    bgGlow: 'radial-gradient(ellipse at top right, rgba(34,164,93,0.16) 0%, transparent 52%)',
  },
  down: {
    accentColor: '#C0392B',
    accentGradient: 'linear-gradient(135deg, #9E2D20 0%, #C0392B 55%, #E05C4A 100%)',
    bgGlow: 'radial-gradient(ellipse at top right, rgba(192,57,43,0.16) 0%, transparent 52%)',
  },
  flat: {
    accentColor: '#8A9AB0',
    accentGradient: 'linear-gradient(135deg, #3A4250 0%, #64748B 55%, #8A9AB0 100%)',
    bgGlow: 'radial-gradient(ellipse at top right, rgba(100,116,139,0.14) 0%, transparent 52%)',
  },
} as const;

/**
 * Get Fear/Greed color config
 */
function getFearGreedConfig(value: number) {
  if (value <= 25) return { color: '#14b8a6', label: 'Extreme Fear' };
  if (value <= 45) return { color: '#10b981', label: 'Fear' };
  if (value <= 55) return { color: '#eab308', label: 'Neutral' };
  if (value <= 75) return { color: '#f97316', label: 'Greed' };
  return { color: '#ef4444', label: 'Extreme Greed' };
}

/**
 * Mini semi-circle gauge for share card
 */
function MiniGauge({ value, size = 64 }: { value: number; size?: number }) {
  const config = getFearGreedConfig(value);
  const needleRotation = -90 + (value / 100) * 180;

  const strokeWidth = 6;
  const radius = (size - strokeWidth) / 2;
  const centerX = size / 2;
  const centerY = size / 2;

  return (
    <div style={{ position: 'relative', width: size, height: size / 2 + 6 }}>
      <svg 
        width={size} 
        height={size / 2 + 6}
        viewBox={`0 0 ${size} ${size / 2 + 6}`}
        style={{ overflow: 'visible' }}
      >
        <defs>
          <linearGradient id="nft-asset-gauge-grad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#14b8a6" />
            <stop offset="25%" stopColor="#10b981" />
            <stop offset="50%" stopColor="#eab308" />
            <stop offset="75%" stopColor="#f97316" />
            <stop offset="100%" stopColor="#ef4444" />
          </linearGradient>
        </defs>
        
        {/* Background arc */}
        <path
          d={`M ${strokeWidth / 2} ${centerY} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${centerY}`}
          fill="none"
          stroke="#1a1a1a"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        
        {/* Colored gradient arc */}
        <path
          d={`M ${strokeWidth / 2} ${centerY} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${centerY}`}
          fill="none"
          stroke="url(#nft-asset-gauge-grad)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        
        {/* Needle */}
        <g transform={`rotate(${needleRotation} ${centerX} ${centerY})`}>
          <line
            x1={centerX}
            y1={centerY - 3}
            x2={centerX}
            y2={centerY - radius * 0.65}
            stroke={config.color}
            strokeWidth={2}
            strokeLinecap="round"
          />
        </g>
        
        {/* Center dot */}
        <circle cx={centerX} cy={centerY} r={3} fill={config.color} />
      </svg>
    </div>
  );
}

/**
 * NFT Asset Share Card
 * Clean, shareable card for individual NFT collection pages with market stats and AI analysis
 * Design follows the cleaner market overview style (not the older social feed style)
 */
const NFTAssetShareCard = forwardRef<HTMLDivElement, NFTAssetShareCardProps>(
  ({ stats, collectionName, collectionSlug, currency = 'ETH', insight, imageUrl, abstractFearGreed, isAbstractChain = false, format = 'page' }, ref) => {
    const isTweet = format === 'tweet';

    // Format helpers
    const formatPrice = (price: number | undefined | null) => {
      if (price === undefined || price === null || price === 0) return '—';
      if (price >= 1) return `${price.toFixed(2)} ${currency}`;
      if (price >= 0.01) return `${price.toFixed(3)} ${currency}`;
      return `${price.toFixed(4)} ${currency}`;
    };

    const formatVolume = (vol: number | undefined | null) => {
      if (vol === undefined || vol === null || vol === 0) return '—';
      if (vol >= 1000) return `${(vol / 1000).toFixed(1)}K ${currency}`;
      if (vol >= 1) return `${vol.toFixed(2)} ${currency}`;
      return `${vol.toFixed(4)} ${currency}`;
    };

    const formatNumber = (num: number | undefined | null) => {
      if (num === undefined || num === null || num === 0) return '—';
      if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
      if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
      return num.toLocaleString();
    };

    const timestamp = new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    const floorChange = stats.oneDayChange || 0;
    // Brand color model: + => green, - => red, 0 => grey (per design system).
    const direction: 'up' | 'down' | 'flat' =
      floorChange > 0 ? 'up' : floorChange < 0 ? 'down' : 'flat';
    const isPositive = direction === 'up';
    const hasChange = direction !== 'flat';

    const glowConfig = DIRECTION_PALETTE[direction];

    // Calculate unique owner percentage
    const ownerPercentage = stats.numOwners && stats.totalSupply && stats.totalSupply > 0
      ? ((stats.numOwners / stats.totalSupply) * 100).toFixed(0)
      : null;

    return (
      <div
        ref={ref}
        style={{
          width: '480px',
          maxWidth: '100%',
          minWidth: '360px',
          padding: '24px',
          background: 'linear-gradient(180deg, #0a0a0a 0%, #171717 100%)',
          borderRadius: '16px',
          fontFamily: '"Space Grotesk", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          position: 'relative',
          boxSizing: 'border-box',
          border: '1px solid #262626',
          overflow: 'hidden',
        }}
      >
        {/* Background glow effect */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            background: glowConfig.bgGlow,
            pointerEvents: 'none',
          }}
        />

        {/* Decorative corner accent */}
        <div
          style={{
            position: 'absolute',
            top: '-50px',
            right: '-50px',
            width: '200px',
            height: '200px',
            background: glowConfig.accentGradient,
            opacity: 0.1,
            borderRadius: '50%',
            filter: 'blur(60px)',
          }}
        />

        {/* === TWEET BRAND BAR (top, always inside the in-feed crop) === */}
        {isTweet && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px',
              position: 'relative',
            }}
          >
            <img src="/descout-logo-dark.png" alt="Descout" style={{ height: '22px', width: 'auto' }} />
            <span style={{ fontSize: '10px', color: '#737373', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 600 }}>
              Daily Spotlight
            </span>
          </div>
        )}

        {/* === HEADER === */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '20px',
            position: 'relative',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Collection Image */}
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={collectionName}
                style={{ width: '44px', height: '44px', borderRadius: '10px', border: '2px solid #262626', objectFit: 'cover' }}
              />
            ) : (
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '10px',
                  background: glowConfig.accentGradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                }}
              >
                🖼️
              </div>
            )}
            <div>
              <div style={{ fontSize: '18px', fontWeight: 700, color: '#fff', letterSpacing: '-0.02em' }}>
                {collectionName}
              </div>
              <div style={{ fontSize: '12px', color: '#737373', marginTop: '2px' }}>
                NFT Collection
              </div>
            </div>
          </div>

          {/* Floor Change Badge — green (+) / red (-) / grey (0) */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '6px 12px',
              background:
                direction === 'up'
                  ? 'linear-gradient(135deg, #1B8049 0%, #22A45D 100%)'
                  : direction === 'down'
                  ? 'linear-gradient(135deg, #9E2D20 0%, #C0392B 100%)'
                  : '#262626',
              borderRadius: '12px',
            }}
          >
            {direction === 'up' ? (
              <TrendingUp style={{ width: '14px', height: '14px', color: '#fff' }} />
            ) : direction === 'down' ? (
              <TrendingDown style={{ width: '14px', height: '14px', color: '#fff' }} />
            ) : (
              <Minus style={{ width: '14px', height: '14px', color: '#8A9AB0' }} />
            )}
            <span style={{ fontSize: '13px', fontWeight: 600, color: hasChange ? '#fff' : '#8A9AB0' }}>
              {isPositive ? '+' : ''}{floorChange.toFixed(1)}%
            </span>
          </div>
        </div>

        {/* === FLOOR PRICE SECTION === */}
        <div
          style={{
            padding: '20px',
            background: '#111',
            borderRadius: '12px',
            marginBottom: '16px',
          }}
        >
          <div style={{ fontSize: '10px', color: '#525252', textTransform: 'uppercase', marginBottom: '8px' }}>
            Floor Price
          </div>
          <div style={{ fontSize: '32px', fontWeight: 700, color: '#fff', fontFamily: 'monospace' }}>
            {formatPrice(stats.floorPrice)}
          </div>
          <div style={{ fontSize: '11px', color: '#525252', marginTop: '8px' }}>
            {timestamp}
          </div>
        </div>

        {/* === KEY STATS ROW === */}
        <div 
          style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            background: '#111',
            borderRadius: '10px',
            marginBottom: '16px',
            position: 'relative',
          }}
        >
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '10px', color: '#525252', textTransform: 'uppercase', marginBottom: '4px' }}>24H Volume</div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>{formatVolume(stats.oneDayVolume)}</div>
          </div>
          <div style={{ width: '1px', height: '32px', background: '#262626', flexShrink: 0 }} />
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '10px', color: '#525252', textTransform: 'uppercase', marginBottom: '4px' }}>Supply</div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>{formatNumber(stats.totalSupply)}</div>
          </div>
          <div style={{ width: '1px', height: '32px', background: '#262626', flexShrink: 0 }} />
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '10px', color: '#525252', textTransform: 'uppercase', marginBottom: '4px' }}>
              {ownerPercentage ? 'Unique Owners' : 'Owners'}
            </div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>
              {ownerPercentage ? `${ownerPercentage}%` : formatNumber(stats.numOwners)}
            </div>
          </div>
        </div>

        {/* === ABSTRACT FEAR & GREED (highlighted, Abstract chain only) === */}
        {isAbstractChain && abstractFearGreed && (() => {
          const fg = getFearGreedConfig(abstractFearGreed.value);
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                padding: '16px 18px',
                background: '#141414',
                border: `1px solid ${fg.color}40`,
                borderRadius: '12px',
                marginBottom: '16px',
              }}
            >
              <MiniGauge value={abstractFearGreed.value} size={72} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '10px', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginBottom: '4px' }}>
                  Abstract Fear &amp; Greed
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                  <span style={{ fontSize: '30px', fontWeight: 700, color: fg.color, fontFamily: 'monospace', lineHeight: 1 }}>
                    {abstractFearGreed.value}
                  </span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: fg.color }}>
                    {abstractFearGreed.classification}
                  </span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* === AI INSIGHT === */}
        {insight?.analysis && (
          <div style={{ marginBottom: '16px', position: 'relative' }}>
            <div style={{ fontSize: '10px', color: '#737373', textTransform: 'uppercase', marginBottom: '8px', fontWeight: 600 }}>
              AI MARKET INSIGHT
            </div>
            <div
              style={{
                fontSize: '12px',
                color: '#a3a3a3',
                lineHeight: 1.6,
                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
                padding: '12px',
                background: '#111',
                borderRadius: '8px',
                // Tweet: clamp to 3 lines so total card height (and the image
                // aspect) stays locked near 4:5 regardless of insight length.
                ...(isTweet
                  ? { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' as const, overflow: 'hidden' }
                  : {}),
              }}
            >
              {insight.analysis}
            </div>
          </div>
        )}

        {/* === FOOTER === */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingTop: '14px',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            position: 'relative',
          }}
        >
          <div style={{ fontSize: '9px', color: '#525252', letterSpacing: '0.04em' }}>
            Data: OpenSea
          </div>
          {/* White wordmark — footer placement for the on-site card. In tweet mode
              the wordmark moves to the top brand bar (crop-safe), so it's omitted
              here to avoid a redundant second mark. */}
          {!isTweet && (
            <img
              src="/descout-logo-dark.png"
              alt="Descout"
              style={{ height: '20px', width: 'auto' }}
            />
          )}
        </div>
      </div>
    );
  }
);

NFTAssetShareCard.displayName = 'NFTAssetShareCard';

export default NFTAssetShareCard;
